export default {
   name: "DataMigration",
   computed: {
      // Checklist grouped by ERP area (Setup, HR, Inventory...), in import order
      checklistGroups() {
         const groups = [];
         this.checklist.forEach((item) => {
            let group = groups.find((g) => g.area === item.area);
            if (!group) {
               group = { area: item.area, items: [], done: 0 };
               groups.push(group);
            }
            group.items.push(item);
            if (item.status === "done" || item.status === "done_with_errors" || item.status === "has_records") {
               group.done++;
            }
         });
         return groups;
      },
   },
	methods: {
      /* ---------- Forms ---------- */
      // Back to step 1. clearAll = true also forgets what was chosen (new import)
      clearForm(clearAll = false) {
         this.isSaving = false;
         this.submitted = false;
         this.migrationData = this.getDefaultMigrationData();
         this.clearImportForm();
         if (clearAll === true) {
            this.formInputs = this.getDefaultFormInputs();
            this.defaultAlert = this.getDefaultAlert();
         }
         this.$nextTick(() => this.$clearValidationStates('#migrationForm'));
      },
      clearImportForm() {
         this.formImportData.dataFile = null;
         this.formImportData.progress = 0;
         this.formImportData.uploading = false;
         this.formImportData.message = "";
         this.formImportData.messageType = "";

         // The File box (FormField) empties itself when dataFile becomes null
      },
      // Checklist group: open this one (closes the others), or close it if already open
      toggleArea(area) {
         this.openArea = this.openArea === area ? "" : area;
      },
      // Checklist arrows: highlight the previous (-1) or next (1) group
      moveArea(step) {
         const groups = this.checklistGroups;
         if (!groups.length) {
            return;
         }
         const current = groups.findIndex((g) => g.area === this.selectedArea);
         const next = Math.min(groups.length - 1, Math.max(0, current + step));
         this.selectedArea = groups[next].area;

         // Moving to another group closes the open one
         if (this.openArea && this.openArea !== this.selectedArea) {
            this.openArea = "";
         }

         this.$nextTick(() => {
            const row = document.querySelector(`.checklist-area[data-area="${CSS.escape(this.selectedArea)}"]`);
            if (row) {
               row.focus();
            }
         });
      },
      // "Import" in the checklist: open the window with the module and company already chosen
      startFromChecklist(item) {
         this.clearForm(true);
         this.formInputs.data_type = item.data_type;
         this.formInputs.target_company_id = this.checklistCompanyId;
      },
      // Filter changed: show the loader and reload the list
      onFilterChange() {
         this.tableData.selectedRecordId = "";
         this.fetchRecords(1, this.$loaderMessages.loaderMessageDefault);
      },
      handleInputChange(event) {
         this.$validateField(event.target);
         this.$clearValidationAlert("#migrationForm");
      },
      onFileImportChange(event) {
         this.formImportData.dataFile = event.target.files[0] || null;
         this.$validateField(event.target);
      },

      /* ---------- Alerts and errors ---------- */
      handleAlert(daOrigin,daMessage,daClass,daPageOrModal){
         // The API sends some messages as an array
         const message = Array.isArray(daMessage) ? daMessage[0] : daMessage;
         this.$displayAlert(daOrigin,message,daClass,daPageOrModal);
      },
      handleError(daOrigin,error,daClass,daPageOrModal) {
         this.button_text= "";
         this.alertType = 'danger';
         if (error && error.data && error.data.message) {
            this.defaultAlert.alertMessage = error.data.message;
         } else {
            this.defaultAlert.alertMessage = error;
         }
         this.showAlert = true;
         this.handleAlert(daOrigin,this.defaultAlert.alertMessage,daClass,daPageOrModal);
         console.error("Error:", this.defaultAlert.alertMessage);
      },
      // true = the session ended and the user was logged out
      handleAuthError(response){
         const code = response.data?.code;
         if (code === "JWT_EXPIRED" || code === "CSRF_INVALID" || code === "AUTH_REQUIRED") {
            this.$forceLogOut("danger", response.data.message);
            return true;
         }
         return false;
      },
      handleNetworkError(error, daPageOrModal){
         if (error.message === "Network Error") {
            this.handleAlert("ending","No internet connection. Please check your network.","danger",daPageOrModal);
         } else {
            this.handleAlert("ending","Something went wrong. Please try again later.","danger",daPageOrModal);
         }
      },

      /* ---------- Upload progress ---------- */
      handleUploadProgress(){
         this.formImportData.uploading = true;
         this.formImportData.progress = 0;
         this.formImportData.message = this.$loaderMessages.uploading;
         this.formImportData.messageType = "info";
      },
      stopUploadProgress(){
         this.formImportData.uploading = false;
      },

      /* ---------- Responses ---------- */
      // Works with the paginated list (records + page info, like CrudTest) and the simple list
      handleFetchSuccess(response){
         const results = response.data.results;

         if (Array.isArray(results)) {
            this.tableData.records = results;
         } else {
            this.tableData.records = results.records || [];
            this.tableData.currentPage = results.current_page;
            this.tableData.totalPages = results.total_pages;
            if (this.tableData.totalRecords > 0) {
               this.lastKnownPage = this.tableData.totalPages;
            }
            this.tableData.hasNextPage = results.hasNextPage;
            this.tableData.totalRecords = results.totalRecords;
            this.tableData.startRecord = results.startRecord;
            this.tableData.endRecord = results.endRecord;
         }

         this.tableData.isVisible = false;

         // Keep the checklist up to date (after imports, undo, refresh)
         if (this.checklistCompanyId) {
            this.fetchChecklist();
         }
      },
      handleUploadSuccess(response){
         const results = response.data.results;
         this.migrationData.batchId = results.batch_id;
         this.migrationData.headers = results.headers;
         this.migrationData.sample_rows = results.sample_rows;
         this.migrationData.columnMap = { ...results.suggested_map };
         this.migrationData.step = 2;
         this.handleAlert("ending",response.data.message,"success","modal");
         this.fetchRecords();
      },
      handleCheckSuccess(response){
         const results = response.data.results;
         this.migrationData.counts = results.counts;
         this.migrationData.problems = results.problems;
         this.migrationData.step = 3;
         this.handleAlert("ending",response.data.message,results.counts.ready > 0 ? "success" : "warning","modal");
         this.fetchRecords();
      },
      handleImportSuccess(response, daPageOrModal){
         const failed = response.data.results.counts.failed > 0;
         this.migrationData.doneMessage = response.data.message;
         if (daPageOrModal === "modal") {
            this.migrationData.step = 4;
         }
         this.handleAlert("ending",response.data.message,failed ? "warning" : "success",daPageOrModal);
      },

      /* ---------- Small helpers for the steps, the list and the checklist ---------- */
      isModalOpen() {
         const modal = document.getElementById("migrationModal");
         return !!modal && modal.classList.contains("show");
      },
      sampleValue(header) {
         const row = this.migrationData.sample_rows.find((r) => (r[header] ?? "") !== "");
         return row ? row[header] : "";
      },
      // The same field chosen for two columns
      isDuplicateChoice(header) {
         const field = this.migrationData.columnMap[header];
         return !!field && Object.entries(this.migrationData.columnMap).some(([h, f]) => h !== header && f === field);
      },
      companyName(id) {
         const company = this.companies.find((c) => Number(c.id) === Number(id));
         return company ? company.company_name : id;
      },
      statusLabel(status) {
         return { uploaded: "Uploaded", checked: "Checked", importing: "Importing", imported: "Imported", undone: "Undone", failed: "Failed" }[status] || status;
      },
      statusClass(status) {
         return {
            uploaded: "bg-secondary",
            checked: "bg-info text-dark",
            importing: "bg-warning text-dark",
            imported: "bg-success",
            undone: "bg-dark",
            failed: "bg-danger",
         }[status] || "bg-secondary";
      },
      checklistStatusLabel(item) {
         return {
            locked: "🔒 Waiting",
            not_started: "Not started",
            in_progress: "In progress",
            has_records: "Has records (added by hand)",
            done: "✓ Done",
            done_with_errors: `✓ Done, ${item.errors} errors`,
         }[item.status] || item.status;
      },
      checklistStatusClass(status) {
         return {
            locked: "bg-secondary",
            not_started: "sa-theme-pill border",
            in_progress: "bg-warning text-dark",
            has_records: "bg-info text-dark",
            done: "bg-success",
            done_with_errors: "bg-warning text-dark",
         }[status] || "bg-secondary";
      },
      // Saves the problem rows as a CSV the user can fix in Excel and upload again
      downloadProblemsCsv(rows, batchId){
         rows.sort((a, b) => a.row_number - b.row_number);
         const headers = Object.keys(rows[0].raw_data || {});
         const cell = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;

         const lines = [[...headers, "Problem"].map(cell).join(",")];
         rows.forEach((r) => {
            lines.push([...headers.map((h) => r.raw_data?.[h]), r.error_message].map(cell).join(","));
         });

         // ﻿ so Excel opens it with the right characters
         const url = window.URL.createObjectURL(new Blob(["﻿" + lines.join("\r\n")], { type: "text/csv;charset=utf-8" }));
         const link = document.createElement('a');
         link.href = url;
         link.setAttribute('download', `import-${batchId}-problems.csv`);
         document.body.appendChild(link);
         link.click();
         document.body.removeChild(link);
         window.URL.revokeObjectURL(url);
      },
   }
}